// invoice-document.reducer.ts
import { createReducer, on } from '@ngrx/store';
import * as InvoiceActions from './invoice-document.actions';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { InvoiceDocumentDetailModel } from '../../models/invoice/invoice-document-detail.model';
import { InvoiceDocumentOverviewModel } from '../../models/invoice/invoice-document-overview.model';

export interface InvoiceDocumentsFeatureState {
  byId: EntityState<InvoiceDocumentDetailModel>; // full docs
  list: EntityState<InvoiceDocumentOverviewModel>; // summaries for listing
}

export const invoiceDocumentsAdapter = createEntityAdapter<InvoiceDocumentDetailModel>();

export const invoiceDocumentOverviewAdapter = createEntityAdapter<InvoiceDocumentOverviewModel>({
  selectId: (d) => d.id!,
});

export const initialState: InvoiceDocumentsFeatureState = {
  byId: invoiceDocumentsAdapter.getInitialState(),
  list: invoiceDocumentOverviewAdapter.getInitialState(),
};

export const invoiceDocumentReducer = createReducer(
  initialState,
  on(
    InvoiceActions.setInvoiceDocumentOverview,
    (state, { payload }): InvoiceDocumentsFeatureState => ({
      ...state,
      list: invoiceDocumentOverviewAdapter.setAll(payload, state.list),
      byId: state.byId,
    }),
  ),
  // ALL GET → write full doc into "byId"
  on(
    InvoiceActions.setInvoiceDocuments,
    (state, { payload }): InvoiceDocumentsFeatureState => ({
      ...state,
      byId: invoiceDocumentsAdapter.setAll(payload, state.byId),
      list: state.list,
    }),
  ),
  // SINGLE GET → write full doc into "byId"
  on(
    InvoiceActions.setSingleInvoiceDocument,
    (state, { payload }): InvoiceDocumentsFeatureState => ({
      ...state,
      byId: invoiceDocumentsAdapter.upsertOne(payload, state.byId),
      list: state.list,
    }),
  ),

  // POST (generated) → usually returns a full doc; add to both slices
  on(
    InvoiceActions.invoiceDocumentGenerated,
    (state, { payload }): InvoiceDocumentsFeatureState => {
      // if your API returns list fields too, map once; otherwise create a minimal list item
      const listItem: InvoiceDocumentOverviewModel = {
        id: payload.id,
        attachments: (payload as any).attachments ?? '',
        creationDate: (payload as any)['creation-date'] ?? null,
        meta: (payload as any)['meta'] ?? null,
      };

      return {
        byId: invoiceDocumentsAdapter.addOne(payload, state.byId),
        list: invoiceDocumentOverviewAdapter.addOne(listItem, state.list),
      };
    },
  ),

  // DELETE → remove from both
  on(
    InvoiceActions.invoiceDocumentOverviewDeleted,
    (state, { payload }): InvoiceDocumentsFeatureState => ({
      byId: invoiceDocumentsAdapter.removeOne(+payload.id!, state.byId),
      list: invoiceDocumentOverviewAdapter.removeOne(+payload.id!, state.list),
    }),
  ),
);
